import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Image, PlusCircle } from "lucide-react";
import { useRoom } from "@/hooks/useRooms";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import type { DateRange } from "@/hooks/useDateRange";
import ExpandSection from "@/components/common/ExpandSection";
import { DataTable } from "@/components/ui/data-table";
import { getReservationColumns } from "./utils/getReservationColumns";
import ImageSelector from "./guests/components/form/ImageSelector";
import { useRoomUpdate } from "./rooms/hooks/useRoomUpdate";
import { RoomQrDialog } from "./rooms/components/RoomQrDialog";
import { createDefaultRoomUsageDateRange } from "@/lib/roomUsage";
import { RoomUsageHistorySection } from "./rooms/components/RoomUsageHistorySection";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { FormField } from "./guests/components/form/FormFields";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditRoomFormData, editRoomSchema } from "./utils/roomSchema";
import RoomStatusField from "./rooms/components/form/StatusField";
import { useRoomCategoriesFiltersSettings } from "@/hooks/useRoomCategories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RoomStatus, UpdateRoomRequest } from "@/types/room";
import { useTranslation } from "react-i18next";

const noop = () => {};

const Field = ({
  label,
  value,
  className,
}: {
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`${className || ""} w-[35%]`}>
      <div className="grid grid-cols-2 gap-6">
        <div className="text-gray-5">{label}</div>
        <div>{value}</div>
      </div>
    </div>
  );
};

export const EditRoomPage = () => {
  const { isLoading, error } = useHotel();
  const hotelId = useCurrentHotelId();

  if (isLoading && !hotelId) {
    return <div>Loading</div>;
  }

  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        Error loading hotel: {error.message}
      </div>
    );
  }

  if (!hotelId) {
    return <div>Loading</div>;
  }

  return <HotelRoom hotelId={hotelId} />;
};

export const HotelRoom: React.FC<{ hotelId: string }> = ({ hotelId }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasReservationsAccess } = useAccessCapabilities();
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const [usageDateRange, setUsageDateRange] = useState<DateRange>(() =>
    createDefaultRoomUsageDateRange(),
  );

  // Fetch room data
  const { data: room, isLoading, error } = useRoom(id!, hotelId);
  const { handleRoomUpdate, isLoading: isUpdating } = useRoomUpdate(
    id!,
    hotelId,
  );
  const { data: settings, isLoading: isLoadingRoomCategoriesSettings } =
    useRoomCategoriesFiltersSettings(hotelId);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<EditRoomFormData>({
    resolver: zodResolver(editRoomSchema),
    defaultValues: {
      number: "",
      categoryId: "",
      status: RoomStatus.AVAILABLE,
      floor: "",
      area: "",
      photoId: "",
    },
    values: room
      ? {
          number: String(room.number ?? ""),
          categoryId: room.categoryId ?? room.category?.id ?? "",
          status: room.status,
          floor: String(room.floor ?? ""),
          area: String(room.area ?? ""),
          photoId: room.photoId ?? "",
        }
      : undefined,
  });

  const selectedImage = watch("photoId");
  const status = watch("status");
  const categoryId = watch("categoryId");
  const currentRoomCategoryId = room?.categoryId ?? room?.category?.id ?? "";
  const currentRoomCategoryName = room?.category?.name ?? "";
  const selectedCategoryId = categoryId || currentRoomCategoryId;
  const categoryOptions = useMemo(() => {
    const categories = settings?.categories ?? [];
    const currentCategory = currentRoomCategoryId
      ? { id: currentRoomCategoryId, name: currentRoomCategoryName }
      : null;

    if (
      !currentCategory ||
      categories.some((category) => category.id === currentCategory.id)
    ) {
      return categories;
    }

    return [currentCategory, ...categories];
  }, [currentRoomCategoryId, currentRoomCategoryName, settings?.categories]);
  const selectedCategory = categoryOptions.find(
    (category) => category.id === selectedCategoryId,
  );

  useEffect(() => {
    if (!currentRoomCategoryId || categoryId) {
      return;
    }

    setValue("categoryId", currentRoomCategoryId);
  }, [categoryId, currentRoomCategoryId, setValue]);

  const reservationColumns = getReservationColumns(
    {},
    { actions: false, selection: false, room: false, status: false },
  );

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const onSubmit = async (data: EditRoomFormData) => {
    if (!room) {
      return;
    }

    const currentCategoryId = room.categoryId ?? room.category?.id ?? "";
    const selectedCategoryId = data.categoryId.trim();
    const updateRequest: UpdateRoomRequest = {
      number: Number(data.number),
      floor: Number(data.floor),
      area: data.area,
      status: data.status,
      ...(selectedCategoryId && selectedCategoryId !== currentCategoryId
        ? { categoryId: selectedCategoryId }
        : {}),
    };

    await handleRoomUpdate(updateRequest, selectedPhotoFile, room.photoId);
    setSelectedPhotoFile(null);
  };

  if (isLoading || isLoadingRoomCategoriesSettings) {
    return (
      <div className="p-12">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/reservations/rooms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="py-8 text-center">
          {t("reservation.roomsForm.loading")}
        </div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/reservations/rooms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="py-8 text-center text-red-600">
          Error loading room details:{" "}
          {(error as any)?.message || "Room not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute top-8 left-8 mb-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate("/reservations/rooms")}
          className="text-gray-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="mx-auto max-w-5xl space-y-6 p-8">
        <h1 className="text-center text-2xl font-semibold">
          {t("reservation.roomsForm.viewTitle")}
        </h1>

        <div className="flex flex-col items-center gap-4">
          <ImageSelector
            defaultIcon={<Image className={"size-[72px] stroke-1"} />}
            overlayContent={
              <div
                className={
                  "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white"
                }
              >
                <PlusCircle className={"size-5"} />
              </div>
            }
            onImageSelect={handleImageUpload}
            selectedImage={selectedImage ?? undefined}
            className="mb-2"
            imageClassName={"size-[72px] rounded-full"}
          />
        </div>

        <div className="text-center">
          <div className="mb-1 text-sm text-gray-600">Room</div>
          <div className="text-2xl font-semibold">{room.number}</div>
        </div>

        <div className="flex justify-center">
          <RoomQrDialog room={room} hotelId={hotelId} />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              id="number"
              label={t("reservation.roomsForm.number")}
              type="number"
              required
              register={register}
              error={errors.number?.message}
              placeholder={t("reservation.roomsForm.numberPlaceholder")}
            />

            <FormField
              id="floor"
              label={t("reservation.roomsForm.floor")}
              type="number"
              required
              register={register}
              error={errors.floor?.message}
              placeholder={t("reservation.roomsForm.floorPlaceholder")}
            />

            <FormField
              id="area"
              label={t("reservation.roomsForm.area")}
              type="number"
              required
              register={register}
              error={errors.area?.message}
              placeholder={t("reservation.roomsForm.areaPlaceholder")}
            />

            <RoomStatusField
              value={status}
              onChange={(value) => setValue("status", value as RoomStatus)}
              error={errors.status?.message}
              withOccupiedStatuses={false}
            />

            <div>
              <label className="mb-1 text-sm font-medium text-gray-700">
                {t("reservation.roomsForm.category")}
              </label>
              <Select
                value={selectedCategoryId}
                onValueChange={(value) =>
                  setValue("categoryId", value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger
                  className={`flex w-full justify-between ${errors.categoryId?.message ? "border-red-500" : "border-black"}`}
                >
                  <SelectValue
                    placeholder={t(
                      "reservation.roomsForm.categoriesPlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.categoryId?.message ? (
                <p className="mt-1 text-sm text-red-500">
                  {errors.categoryId.message}
                </p>
              ) : null}
            </div>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting || isUpdating}
            className="px-8"
          >
            {isSubmitting || isUpdating
              ? t("reservation.roomsForm.saving")
              : t("reservation.roomsForm.saveChanges")}
          </Button>
        </form>

        <ExpandSection
          title={selectedCategory?.name || room.category?.name || ""}
        >
          <Field
            className="mt-4"
            value={
              <div>
                {room.category?.amenities?.map((amenity, index) => (
                  <span key={index}>
                    {amenity}
                    <br />
                  </span>
                ))}
              </div>
            }
            label={"Features"}
          />

          <Field
            value={room.category?.price}
            label={"Price"}
            className="mt-4"
          />
        </ExpandSection>

        {hasReservationsAccess ? (
          <ExpandSection title={"Reservation info"} initiallyExpanded={true}>
            <div className="flex flex-col">
              <label className="mt-4">Previous Reservation</label>
              {room.previousBooking && (
                <DataTable
                  data={[room.previousBooking]}
                  columns={reservationColumns}
                  loading={isLoading}
                  pagination={{
                    currentPage: 0,
                    totalPages: 0,
                    onPageChange: noop,
                  }}
                  sorting={{
                    field: "",
                    direction: "asc",
                    onSort: noop,
                  }}
                />
              )}
              <hr className="mt-2" />
            </div>

            <div className="flex flex-col">
              <label className="mt-4">Current Reservation</label>
              {room.currentBooking && (
                <DataTable
                  data={[room.currentBooking]}
                  columns={reservationColumns}
                  loading={isLoading}
                  pagination={{
                    currentPage: 0,
                    totalPages: 0,
                    onPageChange: noop,
                  }}
                  sorting={{
                    field: "",
                    direction: "asc",
                    onSort: noop,
                  }}
                />
              )}
              <hr className="mt-2" />
            </div>

            <div className="flex flex-col">
              <label className="mt-4">Next Reservation</label>
              {room.nextBooking && (
                <DataTable
                  data={[room.nextBooking]}
                  columns={reservationColumns}
                  loading={isLoading}
                  pagination={{
                    currentPage: 0,
                    totalPages: 0,
                    onPageChange: noop,
                  }}
                  sorting={{
                    field: "",
                    direction: "asc",
                    onSort: noop,
                  }}
                />
              )}
            </div>
          </ExpandSection>
        ) : null}

        {hasReservationsAccess && id ? (
          <RoomUsageHistorySection
            hotelId={hotelId}
            roomId={id}
            dateRange={usageDateRange}
            onDateRangeChange={setUsageDateRange}
          />
        ) : null}
      </div>
    </div>
  );
};
